import React, { useState } from 'react';
import {
  ShieldCheck, Sparkles, User, LayoutGrid, Palette, Users, Trophy,
  MessageCircle, Heart, Award, ChevronDown
} from 'lucide-react';
import useInView from '../hooks/useInView';

const FadeIn = ({ children, delay = 0, className = '' }) => {
  const [ref, inView] = useInView(0.05);
  return (
    <div ref={ref} className={`transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const steps = [
  {
    num: '01',
    icon: ShieldCheck,
    title: 'Parent-aware onboarding',
    short: 'Children join the platform inside a parent-aware onboarding flow.',
    detail: 'ROMIND KIDS opens with a parent-aware onboarding experience. The goal is not a parental control dashboard — the goal is to make sure a child enters the platform inside the right age-aware experience, with safety defaults already in place.',
    color: 'from-purple-500 to-fuchsia-700',
  },
  {
    num: '02',
    icon: Sparkles,
    title: 'Chooses interests',
    short: 'A child picks the themes that actually reflect who they are.',
    detail: 'Instead of an algorithm assigning interests over time, the child explicitly picks the themes that matter to them — creativity, music, reading, photography, friendships, culture, and more. These choices shape the feed, the suggested clubs, and the challenge mix.',
    color: 'from-fuchsia-500 to-pink-700',
  },
  {
    num: '03',
    icon: User,
    title: 'Builds a safe creative profile',
    short: 'Avatars, auras, frames and interests — without sensitive personal data.',
    detail: 'The profile becomes the child\'s visual home on the platform. It is expressive — avatars, auras, frames, interests — but does not expose precise location, school, address, or other sensitive personal data by default.',
    color: 'from-rose-500 to-pink-700',
  },
  {
    num: '04',
    icon: LayoutGrid,
    title: 'Explores Smart Multi-Level Feed',
    short: 'Interest-based discovery instead of one chaotic stream.',
    detail: 'The feed is structured by categories — friends, creativity, photos, videos, clubs, challenges, personal interests — so the child always knows what space they are in. The default is purposeful discovery, not endless scrolling.',
    color: 'from-violet-500 to-purple-700',
  },
  {
    num: '05',
    icon: Palette,
    title: 'Creates content in Create Studio',
    short: 'Posts, photos, videos, stories, moods, and creative projects.',
    detail: 'When the child has something to share, Create Studio gives them age-appropriate tools to make it — visual creation, mood content, stories, photo and video — in a safer publishing environment designed for 9–14.',
    color: 'from-fuchsia-500 to-pink-700',
  },
  {
    num: '06',
    icon: Users,
    title: 'Joins Clubs',
    short: 'Small interest-based spaces built around what they love.',
    detail: 'Clubs are small, interest-based, moderated communities. Children find peers who share the things they care about, with tone and depth calibrated to their age cohort (9–11 or 12–14).',
    color: 'from-indigo-500 to-violet-700',
  },
  {
    num: '07',
    icon: Trophy,
    title: 'Completes Challenges',
    short: 'Gentle creative prompts — participation over competition.',
    detail: 'Challenges are age-aware creative prompts — visual, written, musical, mindful. They turn an open moment into a creative one, without leaderboard pressure or popularity ranking.',
    color: 'from-amber-500 to-orange-700',
  },
  {
    num: '08',
    icon: MessageCircle,
    title: 'Communicates safely in Messenger',
    short: 'Friend-based messaging with reporting, blocking, and RONI guidance.',
    detail: 'Messenger is built around the friends graph, with safety logic woven in: limited unsolicited contact, clear reporting and blocking tools, and RONI safety prompts available right inside conversations.',
    color: 'from-cyan-500 to-blue-700',
  },
  {
    num: '09',
    icon: Heart,
    title: 'Receives RONI guidance when needed',
    short: 'A warm AI companion — guidance, not surveillance.',
    detail: 'RONI surfaces gentle in-context support: kinder phrasing suggestions, flagging unsafe wording, encouragement for new users who haven\'t found friends yet. RONI is companion-first, not a monitor.',
    color: 'from-emerald-500 to-cyan-700',
  },
  {
    num: '10',
    icon: Award,
    title: 'Collects Times Cards & grows through recognition',
    short: 'A long-term recognition arc for creativity and positive participation.',
    detail: 'Over time, positive participation builds toward Times Cards exchanges with friends and the planned long-term Drops recognition path — Bronze, Silver, Gold, Diamond. A real journey of recognition, not a like count.',
    color: 'from-yellow-500 to-amber-700',
  },
];

const HowItWorksSection = () => {
  const [openIdx, setOpenIdx] = useState(null);
  return (
    <section data-testid="how-it-works-section" className="relative py-24 overflow-hidden">
      <div className="absolute top-1/3 right-0 w-80 h-80 bg-purple-600/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-cyan-500/8 rounded-full blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <p className="text-xs font-semibold tracking-[0.2em] uppercase text-purple-400 mb-3 font-outfit">How It Works</p>
          <h2 className="font-outfit font-bold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-3 max-w-3xl">How ROMIND KIDS works.</h2>
          <p className="text-white/55 text-base leading-relaxed mb-12 max-w-2xl">
            A ten-step journey from a child's first day on the platform to their long-term creative life inside it. Tap any step to expand.
          </p>
        </FadeIn>

        {/* Journey rail */}
        <div className="relative">
          {/* Vertical glowing line */}
          <div className="absolute left-[19px] sm:left-[27px] top-2 bottom-2 w-px bg-gradient-to-b from-purple-500/40 via-cyan-400/25 to-fuchsia-500/40" />

          <div className="space-y-3">
            {steps.map((s, i) => {
              const open = openIdx === i;
              return (
                <FadeIn key={s.num} delay={Math.min(i * 40, 300)}>
                  <button
                    data-testid={`step-${s.num}`}
                    onClick={() => setOpenIdx(open ? null : i)}
                    className="group relative w-full text-left rounded-2xl bg-white/[0.03] hover:bg-white/[0.05] border border-white/8 hover:border-purple-400/25 transition-all duration-300 overflow-hidden"
                  >
                    <div className="relative flex items-start gap-4 sm:gap-5 p-4 sm:p-5">
                      {/* Node */}
                      <div className="relative flex-shrink-0">
                        <div className={`w-10 h-10 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center shadow-lg shadow-black/40 transition-transform duration-300 group-hover:scale-105`}>
                          <s.icon size={16} className="text-white sm:hidden" />
                          <s.icon size={22} className="text-white hidden sm:block" />
                        </div>
                        <span className="absolute -top-1.5 -right-2 sm:-right-3 text-[9px] sm:text-[10px] font-outfit font-bold text-white/35 tracking-widest bg-[#0A0816] px-1.5 py-0.5 rounded-full border border-white/8">
                          {s.num}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 justify-between">
                          <h3 className="font-outfit font-semibold text-white text-[14px] sm:text-[15px] tracking-tight">{s.title}</h3>
                          <ChevronDown
                            size={15}
                            className={`text-white/40 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180 text-purple-300' : ''}`}
                          />
                        </div>
                        <p className="text-white/45 text-[12px] sm:text-[13px] leading-relaxed mt-1">{s.short}</p>

                        <div
                          className="grid transition-all duration-500 ease-out"
                          style={{ gridTemplateRows: open ? '1fr' : '0fr' }}
                        >
                          <div className="overflow-hidden">
                            <div className="pt-3 mt-3 border-t border-white/8">
                              <p className="text-white/65 text-[13px] sm:text-[14px] leading-relaxed">{s.detail}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
